import { Component, OnDestroy, OnInit } from '@angular/core';
import { CardsInfoFiltersService, SessionCookieService } from '@docpath/linces-angular-components';
import { BreadcrumbItem, CardInfoFilters, CardInfoFiltersOption } from '@docpath/webcomponents';
import { faClock, faFile, faFileLines, IconDefinition } from '@fortawesome/free-regular-svg-icons';
import { faTriangleExclamation } from '@fortawesome/free-solid-svg-icons';
import { TranslateService } from '@ngx-translate/core';
import { Subscription } from 'rxjs';
import { Constants } from 'src/app/constants/constants';
import { CloudService } from 'src/app/shared/services/cloud.service';
import { environment } from 'src/environments/environment';

import { AppSession } from '../../shared/models/app-session.model';
import { HomeTableService } from './components/table/home-table.service';
import { HomeCardSearch, SearchKeyCardType } from './models/home-card-statistics';
import { HomeService } from './services/home.service';
import { HomeHttpService } from './services/home-http.service';

@Component({
  selector: 'app-home',
  templateUrl: './home.component.html'
})
export class HomeComponent implements OnInit, OnDestroy {

  private readonly i18nPrefix = 'home';

  breadCrumb: BreadcrumbItem[] = [];

  private tableChangedSubscription?: Subscription;

  cardInfoFiltersIndex = [0, 1, 2, 3];
  cardsInfoFilters: CardInfoFilters[] = [];
  private readonly cardsName = 'home-filters';

  idRefreshProcess!: ReturnType<typeof setTimeout>;

  // eslint-disable-next-line max-params
  constructor(
    private readonly homeService: HomeService,
    private readonly homeTableService: HomeTableService,
    private readonly homeHttpService: HomeHttpService,
    private readonly cardsInfoFiltersService: CardsInfoFiltersService,
    private readonly sessionCookieService: SessionCookieService,
    private readonly translateService: TranslateService,
    private readonly cloudService: CloudService
  ) {

    if (this.cardsInfoFiltersService.getCards(this.cardsName).length === 0) {
      const defaultCards = [
        this.cloudService.isModeCloud()
          ? { position: 0, mainFilter: SearchKeyCardType.GeneratedTransactions, secundaryFilter: Constants.NUMBER_OF_HOURS_24 }
          : { position: 0, mainFilter: SearchKeyCardType.GeneratedDocuments, secundaryFilter: Constants.NUMBER_OF_HOURS_24 },
        { position: 1, mainFilter: SearchKeyCardType.MostProductiveHour, secundaryFilter: Constants.NUMBER_OF_HOURS_24 },
        this.cloudService.isModeCloud()
          ? { position: 2, mainFilter: SearchKeyCardType.GeneratedTransactionsMostProductiveHour, secundaryFilter: Constants.NUMBER_OF_HOURS_24 }
          : { position: 2, mainFilter: SearchKeyCardType.GeneratedDocumentsMostProductiveHour, secundaryFilter: Constants.NUMBER_OF_HOURS_24 },
        { position: 3, mainFilter: SearchKeyCardType.GenerationErrors, secundaryFilter: Constants.NUMBER_OF_HOURS_24 }
      ];

      this.cardsInfoFiltersService.updateCards(this.cardsName, defaultCards);
    }

  }

  ngOnInit(): void {

    this.translateService.onLangChange.subscribe(() => {
      this.reloadBreadCrumb();
    });

    this.reloadBreadCrumb();

    this.loadCardsInfoFilters();

    if (!this.tableChangedSubscription) {
      this.tableChangedSubscription = this.homeService.tableChanged.subscribe(() => this.refreshHour());
    }

    const appSession = this.sessionCookieService.getSessionCookie()?.app as AppSession;
    this.initRefreshProcess(appSession.refreshLastHour * 1000);

  }

  private reloadBreadCrumb() {
    this.breadCrumb = [
      { label: environment.APP_NAME, width: '240px' },
      { label: this.translateService.instant(`${this.i18nPrefix}.title`) }
    ];
  }

  private initRefreshProcess(refreshLastHour: number) {

    this.idRefreshProcess = setInterval(() => {
      // Refresh if all home elements to refresh are ready
      if (this.homeService.isHomeElementsReadyToRefresh()) {
        // reset home elements ready to refresh
        this.homeService.resetHomeElementsReadyToRefresh();

        // Refresh cards filters
        this.updateCardsInfoFiltersValue();

        // Refresh Charts and table
        this.homeService.notifyHomeRefreshChange();
      }
    }, refreshLastHour);

  }

  ngOnDestroy() {

    if (this.idRefreshProcess) {
      clearInterval(this.idRefreshProcess);
    }

  }

  private loadCardsInfoFilters() {

    this.cardInfoFiltersIndex.forEach(index => {
      const card = this.cardsInfoFiltersService.getCardByPosition(this.cardsName, index);
      this.loadCardInfoFilters(index, card.mainFilter as SearchKeyCardType, card.secundaryFilter, 3);
    });

  }

  private loadCardInfoFilters(cardNumber: number, mainFilterKey: SearchKeyCardType, secundaryFilter: number, rowsCard: number) {

    this.cardsInfoFilters[cardNumber] = new CardInfoFilters({
      id: cardNumber,
      rows: rowsCard,
      mainFilter: `${this.i18nPrefix}.cards.filters.parameters.${mainFilterKey}.label`,
      mainFilterTooltip: `${this.i18nPrefix}.cards.filters.parameters.${mainFilterKey}.tooltip`,
      mainFiltersOptions: this.generateCardInfoMainFiltersOptions(mainFilterKey),
      secundaryFilter: `${this.i18nPrefix}.cards.filters.number-hours.${this.getNumberHoursLabelByValue(secundaryFilter)}.value`,
      secundaryFilterTooltip: `${this.i18nPrefix}.cards.filters.number-hours.${this.getNumberHoursLabelByValue(secundaryFilter)}.tooltip`,
      secundaryFiltersOptions: this.generateCardInfoSecundaryFiltersOptions(secundaryFilter, mainFilterKey),
      value: '',
      valueIcon: this.getValueIconByParameterKey(mainFilterKey),
      valueTooltip: '',
      valueStyles: { cssClass: this.getCssClassByParameterKey(mainFilterKey) }
    });

    this.updateCardInfoFiltersValue(cardNumber, mainFilterKey, secundaryFilter);

  }

  private getValueIconByParameterKey(key: SearchKeyCardType): IconDefinition | undefined {

    switch (key) {
      case SearchKeyCardType.GeneratedDocuments:
      case SearchKeyCardType.GeneratedTransactions:
        return faFile;
      case SearchKeyCardType.MostProductiveHour:
        return faClock;
      case SearchKeyCardType.GeneratedDocumentsMostProductiveHour:
      case SearchKeyCardType.GeneratedTransactionsMostProductiveHour:
        return faFileLines;
      case SearchKeyCardType.GenerationErrors:
        return faTriangleExclamation;
      default:
        return undefined;
    }

  }

  private updateCardsInfoFiltersValue() {

    this.cardInfoFiltersIndex.forEach(index => {
      const card = this.cardsInfoFiltersService.getCardByPosition(this.cardsName, index);
      this.updateCardInfoFiltersValue(index, card.mainFilter as SearchKeyCardType, card.secundaryFilter);
    });

  }

  private updateCardInfoFiltersValue(cardNumber: number, mainFilterKey: SearchKeyCardType, secundaryFilter: number) {

    const search = this.toSearch(mainFilterKey, secundaryFilter);

    // Show spinner without value (first time)
    if (this.cardsInfoFilters[cardNumber].value === '') {
      this.cardsInfoFilters[cardNumber].loading = true;
    }

    this.homeHttpService.getCardStatistics(search, cardNumber).subscribe(result => {
      this.cardsInfoFilters[cardNumber].value = result;
      this.cardsInfoFilters[cardNumber].valueTooltip = result;
      this.cardsInfoFilters[cardNumber].loading = false;
    });

  }

  private refreshHour(): void {
    this.homeTableService.refreshHour();
  }

  private generateCardInfoMainFiltersOptions(mainFilterKey: SearchKeyCardType): CardInfoFiltersOption[] {

    return [
      this.cloudService.isModeCloud()
        ? new CardInfoFiltersOption({
            active: mainFilterKey === SearchKeyCardType.GeneratedTransactions, title: `${this.i18nPrefix}.cards.filters.parameters.${SearchKeyCardType.GeneratedTransactions}.label`,
            tooltip: `${this.i18nPrefix}.cards.filters.parameters.${SearchKeyCardType.GeneratedTransactions}.tooltip`, value: SearchKeyCardType.GeneratedTransactions
          })
        : new CardInfoFiltersOption({
            active: mainFilterKey === SearchKeyCardType.GeneratedDocuments, title: `${this.i18nPrefix}.cards.filters.parameters.${SearchKeyCardType.GeneratedDocuments}.label`,
            tooltip: `${this.i18nPrefix}.cards.filters.parameters.${SearchKeyCardType.GeneratedDocuments}.tooltip`, value: SearchKeyCardType.GeneratedDocuments
          }),
      new CardInfoFiltersOption({
        active: mainFilterKey === SearchKeyCardType.MostProductiveHour, title: `${this.i18nPrefix}.cards.filters.parameters.${SearchKeyCardType.MostProductiveHour}.label`,
        tooltip: `${this.i18nPrefix}.cards.filters.parameters.${SearchKeyCardType.MostProductiveHour}.tooltip`, value: SearchKeyCardType.MostProductiveHour
      }),
      this.cloudService.isModeCloud()
        ? new CardInfoFiltersOption({
          active: mainFilterKey === SearchKeyCardType.GeneratedTransactionsMostProductiveHour, title: `${this.i18nPrefix}.cards.filters.parameters.${SearchKeyCardType.GeneratedTransactionsMostProductiveHour}.label`,
          tooltip: `${this.i18nPrefix}.cards.filters.parameters.${SearchKeyCardType.GeneratedTransactionsMostProductiveHour}.tooltip`, value: SearchKeyCardType.GeneratedTransactionsMostProductiveHour
        })
        : new CardInfoFiltersOption({
          active: mainFilterKey === SearchKeyCardType.GeneratedDocumentsMostProductiveHour, title: `${this.i18nPrefix}.cards.filters.parameters.${SearchKeyCardType.GeneratedDocumentsMostProductiveHour}.label`,
          tooltip: `${this.i18nPrefix}.cards.filters.parameters.${SearchKeyCardType.GeneratedDocumentsMostProductiveHour}.tooltip`, value: SearchKeyCardType.GeneratedDocumentsMostProductiveHour
        }),
      new CardInfoFiltersOption({
        active: mainFilterKey === SearchKeyCardType.GenerationErrors, title: `${this.i18nPrefix}.cards.filters.parameters.${SearchKeyCardType.GenerationErrors}.label`,
        tooltip: `${this.i18nPrefix}.cards.filters.parameters.${SearchKeyCardType.GenerationErrors}.tooltip`, value: SearchKeyCardType.GenerationErrors
      })
    ];

  }

  private generateCardInfoSecundaryFiltersOptions(secundaryFilter: number, mainFilterKey: SearchKeyCardType): CardInfoFiltersOption[] {

    const options: CardInfoFiltersOption[] = [
      new CardInfoFiltersOption({
        active: secundaryFilter === Constants.NUMBER_OF_HOURS_24, title: `${this.i18nPrefix}.cards.filters.number-hours.${Constants.NUMBER_OF_HOURS_24_LABEL}.value`,
        tooltip: `${this.i18nPrefix}.cards.filters.number-hours.${Constants.NUMBER_OF_HOURS_24_LABEL}.tooltip`, value: Constants.NUMBER_OF_HOURS_24.toString()
      }),
      new CardInfoFiltersOption({
        active: secundaryFilter === Constants.NUMBER_OF_HOURS_12, title: `${this.i18nPrefix}.cards.filters.number-hours.${Constants.NUMBER_OF_HOURS_12_LABEL}.value`,
        tooltip: `${this.i18nPrefix}.cards.filters.number-hours.${Constants.NUMBER_OF_HOURS_12_LABEL}.tooltip`, value: Constants.NUMBER_OF_HOURS_12.toString()
      }),
      new CardInfoFiltersOption({
        active: secundaryFilter === Constants.NUMBER_OF_HOURS_6, title: `${this.i18nPrefix}.cards.filters.number-hours.${Constants.NUMBER_OF_HOURS_6_LABEL}.value`,
        tooltip: `${this.i18nPrefix}.cards.filters.number-hours.${Constants.NUMBER_OF_HOURS_6_LABEL}.tooltip`, value: Constants.NUMBER_OF_HOURS_6.toString()
      })
    ];

    if (mainFilterKey !== SearchKeyCardType.MostProductiveHour
      && mainFilterKey !== SearchKeyCardType.GeneratedDocumentsMostProductiveHour
      && mainFilterKey !== SearchKeyCardType.GeneratedTransactionsMostProductiveHour) {
      options.push(new CardInfoFiltersOption({
        active: secundaryFilter === Constants.NUMBER_OF_HOURS_1, title: `${this.i18nPrefix}.cards.filters.number-hours.${Constants.NUMBER_OF_HOURS_1_LABEL}.value`,
        tooltip: `${this.i18nPrefix}.cards.filters.number-hours.${Constants.NUMBER_OF_HOURS_1_LABEL}.tooltip`, value: Constants.NUMBER_OF_HOURS_1.toString()
      }));
    }

    return options;

  }

  private toSearch(key: SearchKeyCardType, hours: number): HomeCardSearch {
    return new HomeCardSearch(key, hours);
  }

  private getNumberHoursLabelByValue(value: number): string {

    if (value === Constants.NUMBER_OF_HOURS_24) {
      return Constants.NUMBER_OF_HOURS_24_LABEL;
    } else if (value === Constants.NUMBER_OF_HOURS_12) {
      return Constants.NUMBER_OF_HOURS_12_LABEL;
    } else if (value === Constants.NUMBER_OF_HOURS_6) {
      return Constants.NUMBER_OF_HOURS_6_LABEL;
    }
    return Constants.NUMBER_OF_HOURS_1_LABEL;

  }

  private getCssClassByParameterKey(key: SearchKeyCardType): string {

    if (key === SearchKeyCardType.GenerationErrors) {
      return 'text-dpred';
    }
    return '';

  }

  changeCardInfoFilters(card: CardInfoFilters): void {

    const mainFilter = card.mainFiltersOptions.find(f => f.active)?.value as SearchKeyCardType;
    let secundaryFilter = Number(card.secundaryFiltersOptions.find(f => f.active)?.value);

    // If the main filter is MostProductiveHour or GeneratedDocumentsMostProductiveHour or GeneratedTransactionsMostProductiveHour and the secundary filter is 1 hour, change it to 24 hours
    if ((mainFilter === SearchKeyCardType.MostProductiveHour || mainFilter === SearchKeyCardType.GeneratedDocumentsMostProductiveHour || mainFilter === SearchKeyCardType.GeneratedTransactionsMostProductiveHour)
      && secundaryFilter === Constants.NUMBER_OF_HOURS_1) {
      secundaryFilter = Constants.NUMBER_OF_HOURS_24;
    }

    this.cardsInfoFiltersService.updateCard(this.cardsName, { position: card.id, mainFilter, secundaryFilter });

    this.loadCardInfoFilters(card.id, mainFilter, secundaryFilter, card.rows);

  }

}